import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Grid,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { Event, Task, TaskStatus } from '@/types/events';
import { v4 as uuidv4 } from 'uuid';
import { useEvents } from '../../hooks/useEvents';
import { TaskForm } from './TaskForm';

interface TaskManagementProps {
  event: Event;
  onUpdate: (updatedEvent: Event) => void;
}

export const TaskManagement: React.FC<TaskManagementProps> = ({
  event,
  onUpdate
}) => {
  const { updateEventTasks } = useEvents();
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const handleAddTask = () => {
    setSelectedTask(null);
    setIsFormOpen(true);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, task: Task) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setActiveTask(task);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveTask(null);
  };

  const handleDuplicateTask = (task: Task) => {
    const duplicatedTask = {
      ...task,
      id: uuidv4(),
      title: `${task.title} (Copy)`,
      status: 'not_started' as const,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      comments: []
    };
    
    const updatedTasks = [...event.tasks, duplicatedTask];
    onUpdate({
      ...event,
      tasks: updatedTasks,
      // updatedAt: new Date().toISOString()
    });
    handleMenuClose();
  };

  const handleSaveTask = async (taskData: Omit<Task, 'id'>) => {
    const updatedTasks = selectedTask
      ? event.tasks.map(task => task.id === selectedTask.id ? { ...task, ...taskData } : task)
      : [...event.tasks, { ...taskData, id: uuidv4() }];

    const success = await updateEventTasks(event.id, updatedTasks);
    if (success) {
      onUpdate({ ...event, tasks: updatedTasks });
      setIsFormOpen(false);
      setSelectedTask(null);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    const updatedTasks = event.tasks.filter(t => t.id !== taskId);
    const success = await updateEventTasks(event.id, updatedTasks);
    if (success) {
      onUpdate({ ...event, tasks: updatedTasks });
    }
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'blocked':
        return 'error';
      default:
        return 'default';
    }
  };

  const getCompletionRate = () => {
    const total = event.tasks.length;
    if (total === 0) return 0;
    const completed = event.tasks.filter(task => task.status === 'completed').length;
    return (completed / total) * 100;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Task Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddTask}
          >
            Add Task
          </Button>
        </Stack>

        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Task Progress
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Completion Rate
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(getCompletionRate())}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={getCompletionRate()}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        </Paper>

        <Grid container spacing={2}>
          {event.tasks.map((task) => (
            <Grid item xs={12} key={task.id}>
              <Paper sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Box>
                      <Typography variant="subtitle1">
                        {task.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {task.description}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="More actions">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, task)}
                        >
                          <MoreIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={task.status.replace('_', ' ')}
                      color={getStatusColor(task.status)}
                      size="small"
                    />
                    <Chip
                      label={task.priority}
                      color={task.priority === 'high' ? 'error' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Due: {new Date(task.dueDate).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Assigned to: {task.assignedTo}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem 
          onClick={() => {
            if (activeTask) {
              setSelectedTask(activeTask);
              setIsFormOpen(true);
            }
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (activeTask) handleDuplicateTask(activeTask);
          }}
        >
          <ListItemIcon>
            <CopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (activeTask) handleDeleteTask(activeTask.id);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <TaskForm
        open={isFormOpen}
        task={selectedTask}
        onClose={() => setIsFormOpen(false)}
        onSave={handleSaveTask}
      />
    </Box>
  );
};